import React from 'react';
import { Phone, Mail, MapPin, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicyPage = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <Helmet>
        <title>Privacy Policy | SHIPPZY</title>
        <meta name="description" content="Shippzy's Privacy Policy. Learn how we collect, use, and protect your personal information for international shipping services from Canada." />
        <link rel="canonical" href="https://www.shippzy.ca/privacy-policy" />
      </Helmet>
      
      <div className="min-h-screen bg-white">
      {/* Print Button - Hidden in Print */}
      <div className="no-print fixed top-4 right-4 z-50">
        <Button 
          onClick={handlePrint}
          className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white shadow-lg"
        >
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
      </div>

      {/* Document Container */}
      <div className="max-w-4xl mx-auto p-8 md:p-12">
        {/* Letterhead Header */}
        <div className="border-b-4 border-red-600 pb-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_angry-ellis-7/artifacts/rd8nrfx9_IMG_7688.png" 
                alt="Shippzy Logo" 
                className="h-16 w-16 rounded-full"
              />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">SHIPPZY INC.</h1>
                <p className="text-yellow-600 font-semibold">Ship with Shippzy</p>
              </div>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-600 space-y-1">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>140 Advance Blvd, Unit 7, Brampton, ON L6T 4Z8, Canada</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span>+1 (437) 616-0758</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>ship@shippzy.ca</span>
            </div>
          </div>
        </div>

        {/* Document Content */}
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h2>
            <p className="text-gray-600">Last Updated: March 6, 2026</p>
          </div>

          <div className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed">
              This Privacy Policy describes how Shippzy Inc. ("Shippzy", "we", "our", or "us") collects, uses, discloses, and protects personal information when users access or use the website Shippzy.ca, our services, or interact with us in connection with shipping, logistics, or courier-related services.
            </p>

            <p className="text-gray-700 leading-relaxed">
              Shippzy is committed to protecting your privacy and handling personal information in accordance with applicable privacy laws in Canada, including the Personal Information Protection and Electronic Documents Act ("PIPEDA").
            </p>

            <p className="text-gray-700 leading-relaxed">
              By using our website or services, you consent to the collection and use of your information as outlined in this Privacy Policy.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">1. Company Information</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700"><strong>Company Name:</strong> Shippzy Inc.</p>
              <p className="text-gray-700"><strong>Brand Names:</strong> Shippzy / ShippzyCA / Shippzy.ca</p>
              <p className="text-gray-700"><strong>Founder & CEO:</strong> Saksham Gupta</p>
              <p className="text-gray-700"><strong>Business Number:</strong> 762433423RC0001</p>
              <p className="text-gray-700"><strong>Corporation Number:</strong> 1618047-7</p>
              <p className="text-gray-700"><strong>Head Office:</strong> 140 Advance Blvd, Unit 7, Brampton, Ontario L6T 4Z8, Canada</p>
              <p className="text-gray-700"><strong>Contact Email:</strong> ship@shippzy.ca</p>
              <p className="text-gray-700"><strong>Phone:</strong> +1 (437) 616-0758</p>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">2. Information We Collect</h3>
            <p className="text-gray-700 leading-relaxed">
              Shippzy may collect personal information that customers voluntarily provide when using our services or interacting with our website. This may include, but is not limited to:
            </p>

            <div className="ml-6 space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Personal Identification Information</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Pickup address</li>
                  <li>Delivery address</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Shipment Information</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>Package dimensions and weight</li>
                  <li>Package contents description</li>
                  <li>Declared value of items</li>
                  <li>Customs-related documentation when applicable</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Communication Information</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>Customer support inquiries</li>
                  <li>Emails, phone communications, or service requests</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Website Usage Data</h4>
                <p className="text-gray-700">When users visit our website, certain technical data may automatically be collected, including:</p>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>IP address</li>
                  <li>Browser type</li>
                  <li>Device information</li>
                  <li>Website interaction data</li>
                  <li>Cookies and tracking technologies</li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">3. How We Use Your Information</h3>
            <p className="text-gray-700 leading-relaxed">Shippzy may use collected information for the following purposes:</p>
            <ul className="list-disc ml-6 text-gray-700 space-y-2">
              <li>Processing and arranging shipping services</li>
              <li>Generating shipment labels and documentation</li>
              <li>Coordinating logistics and courier pickup or delivery</li>
              <li>Providing shipment tracking updates</li>
              <li>Customer service and support</li>
              <li>Improving website functionality and services</li>
              <li>Fraud prevention and security monitoring</li>
              <li>Compliance with legal or regulatory obligations</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              We may also use customer information to provide service notifications and operational communications related to shipments.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">4. Payment Processing</h3>
            <p className="text-gray-700 leading-relaxed">
              Shippzy may process payments for shipping services using third-party payment processors. Payment methods may include credit or debit cards, online payments, and in-person payments.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Payments may be processed through secure third-party payment platforms including Square and PayPal.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">
              Shippzy does not store full payment card details on its servers. Payment information is handled directly by the respective payment processors in accordance with their own privacy and security policies.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">5. Sharing of Information</h3>
            <p className="text-gray-700 leading-relaxed">
              To provide shipping services, Shippzy may share relevant shipment information with third-party logistics providers and carriers. These carriers may include DHL, FedEx, UPS, Purolator, airline cargo carriers or other logistics partners.
            </p>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 my-4">
              <p className="text-gray-800 font-semibold">Important Notice:</p>
              <p className="text-gray-700">
                Shippzy acts as an independent shipping facilitator and logistics service provider. The use of third-party carriers does not necessarily indicate that Shippzy is an authorized dealer, agent, or official partner of any carrier unless explicitly stated.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Information shared with carriers is limited to what is necessary to complete shipping services.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">6. Cookies and Website Tracking</h3>
            <p className="text-gray-700 leading-relaxed">
              The Shippzy website may use cookies and similar tracking technologies to improve website functionality, analyze website traffic, understand user behavior, and enhance user experience.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Cookies may collect technical information such as device type, browser information, IP address, and website pages visited. Users can control or disable cookies through their browser settings.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">7. Data Storage and Security</h3>
            <p className="text-gray-700 leading-relaxed">
              Personal information collected by Shippzy is stored using secure cloud infrastructure and servers located primarily in Canada.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We implement reasonable administrative, technical, and physical safeguards to protect personal information from unauthorized access, disclosure, alteration, and misuse.
            </p>
            <p className="text-gray-700 leading-relaxed italic">
              Despite these measures, no data transmission over the internet can be guaranteed to be completely secure.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">8. Data Retention</h3>
            <p className="text-gray-700 leading-relaxed">
              Shippzy retains personal information only for as long as necessary to provide shipping services, maintain transaction records, comply with legal obligations, and resolve disputes or service inquiries.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Shipment records and related data may be retained for operational, accounting, or legal purposes.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">9. Customer Privacy Rights</h3>
            <p className="text-gray-700 leading-relaxed">Customers may request to:</p>
            <ul className="list-disc ml-6 text-gray-700 space-y-2">
              <li>Access personal information held by Shippzy</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of certain personal data where permitted by law</li>
              <li>Opt out of marketing communications</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Requests may be submitted by contacting us using the information listed below.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">10. Third-Party Websites</h3>
            <p className="text-gray-700 leading-relaxed">
              Our website may contain links to third-party websites or external services. Shippzy is not responsible for the privacy practices or policies of external websites. Users should review the privacy policies of any third-party sites they visit.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">11. Changes to This Privacy Policy</h3>
            <p className="text-gray-700 leading-relaxed">
              Shippzy may update this Privacy Policy from time to time to reflect changes in our services, technology, or legal requirements. Updated policies will be posted on the Shippzy website with a revised "Last Updated" date.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Continued use of our services after changes indicates acceptance of the updated policy.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">12. Contact Information</h3>
            <p className="text-gray-700 leading-relaxed">
              For questions regarding this Privacy Policy or to make privacy-related requests, please contact:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <p className="text-gray-900 font-bold mb-2">Shippzy Inc.</p>
              <p className="text-gray-700">140 Advance Blvd, Unit 7, Brampton, Ontario L6T 4Z8, Canada</p>
              <p className="text-gray-700">Email: ship@shippzy.ca</p>
              <p className="text-gray-700">Phone: +1 (437) 616-0758</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t-2 border-gray-300 mt-12 pt-6">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <p>© 2026 Shippzy Inc. All Rights Reserved.</p>
            <p>Document ID: PRIVACY-2026-03-06</p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>
    </div>
    </>
  );
};

export default PrivacyPolicyPage;